import { Autocomplete, Box, Button, Checkbox, Divider, FormControlLabel, FormHelperText, TextField, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IdIssuanceVerifyFormInputs } from 'src/@types/pendingRespondentStatus';
import RHFTextField from 'src/components/react-hook-form/RHFTextField';
import { OrganizationEntity } from 'src/features/general/enquete-answer/store/enqueteAnswerSlice';
import { useIdIssuanceForm } from '../hooks/useIdIssuanceForm';
import { ServiceGuideBox } from './ServiceGuideBox';
import { TermsOfServiceBox } from './TermsOfServiceBox';

type IdIssuanceFormProps = {
  token: string | null;
  organizations: OrganizationEntity[];
};

export const IdIssuanceForm = ({ token, organizations }: IdIssuanceFormProps) => {
  const { t } = useTranslation();
  const { onSubmit, canAgree, setCanAgree } = useIdIssuanceForm({ token });
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isValid, isDirty },
  } = useForm<IdIssuanceVerifyFormInputs>({
    mode: 'onChange',
  });

  const isFormValid = isValid && isDirty;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontSize: { xs: '1.5rem', md: '2rem' },
              fontWeight: 'bold',
            }}>
            {t('idIssuance.title')}
          </Typography>
          <ServiceGuideBox />
          <Box>
            <TermsOfServiceBox canAgree={canAgree} setCanAgree={setCanAgree} />
            <Controller
              name="agreement"
              control={control}
              defaultValue={false}
              rules={{ required: t('idIssuance.form.validation.agreement.required') }}
              render={({ field }) => {
                return (
                  <FormControlLabel
                    control={<Checkbox {...field} disabled={!canAgree} checked={field.value} />}
                    label={canAgree ? t('idIssuance.form.label.agreement.agree') : t('idIssuance.form.label.agreement.read')}
                  />
                );
              }}
            />
            {errors.agreement && <FormHelperText error>{errors.agreement.message}</FormHelperText>}
          </Box>
          <Divider />
          <Typography variant="h6">{t('idIssuance.form.formTitle.companyName')}</Typography>
          <Controller<IdIssuanceVerifyFormInputs>
            name="organizationId"
            control={control}
            rules={{ required: t('idIssuance.form.validation.companyName.required') }}
            render={({ field: { onChange, value, ref } }) => (
              <Autocomplete<OrganizationEntity, false>
                options={organizations}
                getOptionLabel={(option: OrganizationEntity) => `${option.companyNameJpn} / ${option.companyNameEng}`}
                onChange={(_, newValue) => {
                  onChange(newValue ? newValue.id : '');
                }}
                value={organizations.find((org) => org.id === value) || null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputRef={ref}
                    label={t('idIssuance.form.label.companyName')}
                    error={!!errors.organizationId}
                    helperText={errors.organizationId?.message}
                    required
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
              />
            )}
          />

          <Typography variant="h6">{t('idIssuance.form.formTitle.email')}</Typography>
          <RHFTextField
            label={t('idIssuance.form.label.email')}
            control={control}
            name="email"
            rules={{
              required: t('idIssuance.form.validation.email.required'),
              pattern: {
                value:
                  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                message: t('idIssuance.form.validation.email.pattern'),
              },
              maxLength: {
                value: 132,
                message: t('idIssuance.form.validation.email.maxLength'),
              },
            }}
            required
            fullWidth
            placeholder={t('idIssuance.form.placeHolder.email')}
            InputProps={{
              autoComplete: 'email',
            }}
          />
          <Typography variant="h6">{t('idIssuance.form.formTitle.nameJpn')}</Typography>
          <RHFTextField
            label={t('idIssuance.form.label.userLastNameJpn')}
            control={control}
            name="userLastNameJpn"
            rules={{
              maxLength: {
                value: 32,
                message: t('idIssuance.form.validation.userLastNameJpn.maxLength'),
              },
            }}
            fullWidth
            placeholder={t('idIssuance.form.placeHolder.userLastNameJpn')}
            InputProps={{
              autoComplete: 'userLastNameJpn',
            }}
          />
          <RHFTextField
            label={t('idIssuance.form.label.userFirstNameJpn')}
            control={control}
            name="userFirstNameJpn"
            rules={{
              maxLength: {
                value: 32,
                message: t('idIssuance.form.validation.userFirstNameJpn.maxLength'),
              },
            }}
            fullWidth
            placeholder={t('idIssuance.form.placeHolder.userFirstNameJpn')}
            InputProps={{
              autoComplete: 'userFirstNameJpn',
            }}
          />
          <Typography variant="h6">{t('idIssuance.form.formTitle.nameKana')}</Typography>
          <RHFTextField
            label={t('idIssuance.form.label.userLastNameKana')}
            control={control}
            name="userLastNameKana"
            rules={{
              maxLength: {
                value: 32,
                message: t('idIssuance.form.validation.userLastNameKana.maxLength'),
              },
              pattern: {
                value: /^[\u{3000}-\u{301C}\u{30A0}-\u{30FF}\u{30FB}-\u{30FE}\u{FF61}-\u{FF9F}\da-zA-Z.,()（）、。\s]+$/mu,
                message: t('idIssuance.form.validation.userLastNameKana.pattern'),
              },
            }}
            fullWidth
            placeholder={t('idIssuance.form.placeHolder.userLastNameKana')}
            InputProps={{
              autoComplete: 'userLastNameKana',
            }}
          />
          <RHFTextField
            label={t('idIssuance.form.label.userFirstNameKana')}
            control={control}
            name="userFirstNameKana"
            rules={{
              maxLength: {
                value: 32,
                message: t('idIssuance.form.validation.userFirstNameKana.maxLength'),
              },
              pattern: {
                value: /^[\u{3000}-\u{301C}\u{30A0}-\u{30FF}\u{30FB}-\u{30FE}\u{FF61}-\u{FF9F}\da-zA-Z.,()（）、。\s]+$/mu,
                message: t('idIssuance.form.validation.userFirstNameKana.pattern'),
              },
            }}
            fullWidth
            placeholder={t('idIssuance.form.placeHolder.userFirstNameKana')}
            InputProps={{
              autoComplete: 'userFirstNameKana',
            }}
          />
          <Typography variant="h6">{t('idIssuance.form.formTitle.nameEng')}</Typography>
          <RHFTextField
            label={t('idIssuance.form.label.userLastNameEng')}
            control={control}
            name="userLastNameEng"
            rules={{
              required: t('idIssuance.form.validation.userLastNameEng.required'),
              maxLength: {
                value: 32,
                message: t('idIssuance.form.validation.userLastNameEng.maxLength'),
              },
              pattern: {
                value: /^[A-Za-z0-9_-]+$/,
                message: t('idIssuance.form.validation.userLastNameEng.pattern'),
              },
            }}
            required
            fullWidth
            placeholder={t('idIssuance.form.placeHolder.userLastNameEng')}
            InputProps={{
              autoComplete: 'userLastNameEng',
            }}
          />
          <RHFTextField
            label={t('idIssuance.form.label.userFirstNameEng')}
            control={control}
            name="userFirstNameEng"
            rules={{
              required: t('idIssuance.form.validation.userFirstNameEng.required'),
              maxLength: {
                value: 32,
                message: t('idIssuance.form.validation.userFirstNameEng.maxLength'),
              },
              pattern: {
                value: /^[A-Za-z0-9_-]+$/,
                message: t('idIssuance.form.validation.userFirstNameEng.pattern'),
              },
            }}
            required
            fullWidth
            placeholder={t('idIssuance.form.placeHolder.userFirstNameEng')}
            InputProps={{
              autoComplete: 'userFirstNameEng',
            }}
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={isSubmitting || !isFormValid}
            sx={{ mt: 2, width: 200, display: 'block', ml: 'auto', mr: 0 }}>
            {isSubmitting ? t('idIssuance.form.button.submitting') : t('idIssuance.form.button.submit')}
          </Button>
        </Box>
      </form>
    </Box>
  );
};
